import type { Task } from "~skillBuilder/utils/types";

import { image, title } from "../../contentBlocks";

export const TspSkillCheck = {
  name: "Skill Check",
  uniqueName: "tsp-skill-check",
  icon: "BookmarkCheck",
  durationTimeText: "10 min",
  isQuiz: true,
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      prevButtonText: "Review",
      showPreviousButton: true,
      questions: [
        // Question 1.1
        {
          attribute: "the-sub-playbook.skill-check/intro",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Skill Check", durationTimeText: "10 min" })}
<p>Show what you&rsquo;ve learned! Complete this quiz to earn a qualification for your profile.</p>
<div style="text-align:center;max-width:250px;width:100%;margin-top:24px;margin-left:auto;margin-right:auto;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/v1731113489/skillbuilderAssets/optimized/woman-and-clipboard.png", alt: "Illustrated woman standing in front of a checked clipboard." })}
</div>
`,
          },
        },
      ],
    },
    // Step 2
    {
      questions: [
        // Question 2.1
        {
          attribute: "the-sub-playbook.skill-check/support-resource",
          label:
            "If you encounter a technical issue with the Swing platform or need guidance on school standards while on the job, what is the best resource for immediate assistance?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "Wait until the end of the day to send an email",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "Reach out to the Swing Support Team via Live Chat or phone.",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "Ask the students for help with the app.",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label: "Leave the assignment early to visit a Swing office.",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    // Step 3
    {
      questions: [
        // Question 3.1
        {
          attribute: "the-sub-playbook.skill-check/multi-day",
          label:
            "You have accepted a multi-day request but realize you cannot make it on the second day. What is the most professional course of action?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "Simply don't show up on the day you are unavailable.",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "Cancel the entire multi-day request immediately.",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label:
                  "Contact the Swing Support Team to see if the school can adjust the schedule or use the partial cancellation feature.",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "Ask another substitute teacher you know to cover for you.",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    // Step 4
    {
      questions: [
        // Question 4.1
        {
          attribute: "the-sub-playbook.skill-check/power-phrase",
          label:
            'Which of the following is a recommended "Power Phrase" for de-escalating a frustrated student while maintaining professional boundaries?',
          answerType: {
            type: "RADIO",
            options: [
              {
                label: '"I\'m the teacher, and you need to listen to me right now."',
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label:
                  "\"I can see you're frustrated; let's find a way to get this done together.\"",
                value: "valid",
                isCorrect: true,
              },
              {
                label: '"If you don\'t calm down, I will have to call the principal."',
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label: '"Why are you acting like this in front of your friends?"',
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    // Step 5
    {
      questions: [
        // Question 5.1
        {
          attribute: "the-sub-playbook.skill-check/active-presence",
          label:
            'To build instant rapport and maintain an "Active Presence," what should a substitute teacher do instead of sitting at the teacher\'s desk?',
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "Sit in the back of the room to watch everyone.",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "Use the time to catch up on personal reading or emails.",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label:
                  "Continuously circulate the room and use student names to acknowledge effort.",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "Stand by the door to ensure no one leaves early.",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    // Step 6
    {
      questions: [
        // Question 6.1
        {
          attribute: "the-sub-playbook.skill-check/responsive-approach",
          label:
            'When a student displays unwanted behavior, what does a "Responsive" (rather than Reactive) approach focus on?',
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "Shutting the student down immediately to maintain authority.",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label:
                  "Shaming the student in front of peers to prevent others from following suit.",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label:
                  'Understanding the "why" behind the behavior and calmly guiding them back to the task.',
                value: "valid",
                isCorrect: true,
              },
              {
                label: "Assigning a punitive consequence right away, such as isolation.",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    // Step 7
    {
      questions: [
        // Question 7.1
        {
          attribute: "the-sub-playbook.skill-check/scaffolding-support",
          label:
            'How can a substitute teacher use "Scaffolding" to support a student who is struggling with a new assignment without doing the work for them?',
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "Give the student the answer key to help them finish faster.",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "Break the task into manageable chunks and use visual aids or diagrams.",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "Tell the student to skip the assignment if it's too hard.",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label: "Move the student's desk closer to the front so they can see better.",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    // Step 8
    {
      questions: [
        // Question 8.1
        {
          attribute: "the-sub-playbook.skill-check/inclusive-language",
          label: "What does using inclusive language in the classroom help promote?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "A sense of safety and belonging for all students.",
                value: "valid",
                isCorrect: true,
              },
              { label: "A more serious academic tone.", value: "invalid-1", isCorrect: false },
              {
                label: "Established and uniform behavior among all students.",
                value: "invalid-2",
                isCorrect: false,
              },
              { label: "A classroom free of distractions.", value: "invalid-3", isCorrect: false },
            ],
          },
        },
      ],
    },
    // Step 9
    {
      questions: [
        // Question 9.1
        {
          attribute: "the-sub-playbook.skill-check/behavior-communication",
          label: "Why is it important to remember that behavior is often a form of communication?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "It allows you to ignore most behavior.",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "It encourages empathy and curiosity toward student needs.",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "It allows you to assign punishment right away.",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label: "It ensures students always follow directions the first time.",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    // Step 10
    {
      questions: [
        // Question 10.1
        {
          attribute: "the-sub-playbook.skill-check/power-struggle",
          label: "What’s a helpful strategy to avoid a power struggle?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "Engage the student in back-and-forth arguments.",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "Reiterate expectations calmly and consistently.",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "Challenge and talk back to the student in front of their peers.",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label: "Show visible frustration to assert your authority.",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    // Step 11
    {
      questions: [
        // Question 11.1
        {
          attribute: "the-sub-playbook.skill-check/struggling-confidence",
          label: "Which of the following best supports a struggling student’s confidence?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label:
                  "Remind them that they should know this material and others are doing better to encourage them to work harder.",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label:
                  "Tell them to try harder while providing zero support so they learn on their own.",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label:
                  "Avoid interacting with students who are struggling so as to not embarrass them.",
                value: "invalid-3",
                isCorrect: false,
              },
              {
                label:
                  "Celebrate their small wins, and offer positivity and encouragement along the way.",
                value: "valid",
                isCorrect: true,
              },
            ],
          },
        },
      ],
    },
    // Step 12
    {
      nextButtonText: "Great!",
      questions: [
        // Question 12.1
        {
          attribute: "the-sub-playbook.skill-check/outro",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<div style="text-align: center;">
  ${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1731608485/skillbuilderAssets/optimized/skill_builder_complete.jpg", alt: "illustrated man holding trophy standing next to a woman" })}
  <p><strong>You&apos;ve completed this skill!</strong></p>
  <p>Thank you for your commitment to developing your teaching skills. You&apos;ll now see <strong>The Sub Playbook</strong> as a qualification on your profile.</p>
</div>
`,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
